import { useState, type FormEvent } from 'react';
import { useAppDispatch } from '../../../Redux/hooks';
import { createLesson } from '../../../Redux/lessonsSlice';
import { useTranslation } from '../../../i18n/useTranslation';
import { ApiError } from '../../../Models/ApiModel';
import type { StudentModel } from '../../../Models/StudentModel';
import { formatDate } from '../../../Utils/DateUtil';
import { studentDisplayName } from '../../../Utils/DisplayNameUtil';
import { localDateString } from '../../../Utils/LessonStatusUtil';
import { Modal } from '../../SharedArea/Modal/Modal';
import { Spinner } from '../../SharedArea/Spinner/Spinner';
import './MultiDateAdd.css';

/**
 * Booking a run of lessons for one student in a single pass.
 *
 * A tutor setting up a term enters the same student a dozen times through the
 * single-lesson form. Here the student and the amount are fixed and only the
 * dates change, which is the shape of the actual task.
 *
 * The time is left empty by default so the server inherits the student's
 * recurring hour for each weekday, which is right more often than any single
 * time the form could pick.
 */
export function MultiDateAdd({ student, onClose }: { student: StudentModel; onClose: () => void }) {
  const { tr, language } = useTranslation();
  const dispatch = useAppDispatch();
  const [dates, setDates] = useState<string[]>([]);
  const [pending, setPending] = useState(localDateString());
  const [time, setTime] = useState('');
  const [amount, setAmount] = useState(student.price);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function addDate() {
    if (!pending || dates.includes(pending)) return;
    setDates(current => [...current, pending].sort());
  }

  function removeDate(date: string) {
    setDates(current => current.filter(item => item !== date));
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (dates.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      // One at a time and in order, so a failure part way through leaves the
      // earlier dates saved rather than rolling the whole batch back. The
      // tutor sees what landed and can retry the rest.
      for (const date of dates) {
        await dispatch(createLesson({
          student: student.name,
          date,
          amount,
          ...(time ? { time } : {})
        })).unwrap();
      }
      onClose();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.code : 'SERVER_ERROR');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={tr('addManyDates')}
      subtitle={studentDisplayName(student, language)}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ghost-button" onClick={onClose}>{tr('cancel')}</button>
          <button type="submit" form="multi-date-add" className="primary-button" disabled={busy || dates.length === 0 || !amount}>
            {/* Hebrew has no plural form that covers one and many, so the
                label is three strings rather than a count wedged into one. */}
            {busy
              ? <Spinner inline />
              : dates.length === 0
                ? tr('addManyDates')
                : dates.length === 1
                  ? tr('addLesson')
                  : tr('addManyDatesSave', dates.length)}
          </button>
        </>
      }
    >
      <form id="multi-date-add" className="multi-date" onSubmit={submit}>
        <div className="multi-date-picker">
          <label className="field">
            <span>{tr('date')}</span>
            <input className="text-input" type="date" value={pending} onChange={e => setPending(e.target.value)} />
          </label>
          <button type="button" className="ghost-button" onClick={addDate} disabled={!pending || dates.includes(pending)}>
            {tr('addDate')}
          </button>
        </div>

        {dates.length > 0 && (
          <ul className="multi-date-list">
            {dates.map(date => (
              <li key={date}>
                <bdi>{formatDate(date, language)}</bdi>
                <button type="button" onClick={() => removeDate(date)} aria-label={tr('remove')}>×</button>
              </li>
            ))}
          </ul>
        )}

        <div className="multi-date-fields">
          <label className="field">
            <span>{tr('time')}</span>
            <input className="text-input" type="time" value={time} onChange={e => setTime(e.target.value)} />
          </label>
          <label className="field">
            <span>{tr('amount')}</span>
            <input
              className="text-input"
              inputMode="numeric"
              value={amount}
              onChange={e => setAmount(e.target.value.replace(/\D/g, ''))}
              required
            />
          </label>
        </div>
        <p className="multi-date-hint">{tr('addManyDatesHint')}</p>
        {error && <p className="quick-add-error" role="alert">{tr('errorGeneric')}</p>}
      </form>
    </Modal>
  );
}
